import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const ContactForm = ({ defaultPackage = "", compact = false, testidPrefix = "contact" }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        name: "",
        company: "",
        industry: "",
        phone: "",
        email: "",
        website: "",
        package: defaultPackage,
        timeline: "",
        budget: "",
        services: [],
        message: "",
        consent: false,
    });

    const onChange = (k) => (e) => {
        const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: val }));
    };

    const toggleService = (s) => {
        setForm((f) => ({
            ...f,
            services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
        }));
    };

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Wpisz imię i nazwisko";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Podaj prawidłowy adres e-mail";
        if (form.message.trim().length < 10) e.message = "Opisz krótko swój projekt (min. 10 znaków)";
        if (!form.consent) e.consent = "Wymagana zgoda";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Dziękujemy! Skontaktujemy się z Tobą.", { duration: 5000 });
            navigate("/kontakt?success=1");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Wystąpił błąd. Spróbuj ponownie.";
            toast.error(typeof msg === "string" ? msg : "Wystąpił błąd. Spróbuj ponownie.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full rounded-[12px] border border-[#E5E7EB] bg-white px-4 py-3 text-[15px] text-[#0B1220] placeholder-[#0B1220]/40 focus:outline-none focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20 transition-colors";
    const labelCls = "block text-sm font-semibold text-[#0B1220] mb-1.5";
    const allServices = [
        "Strona internetowa",
        "Aplikacja",
        "Branding",
        "Social media",
        "Prowadzenie social mediów – 799 zł / miesiąc",
        "Marketing",
        "Automatyzacja AI",
        "Inna usługa",
    ];

    return (
        <form onSubmit={onSubmit} data-testid={`${testidPrefix}-form`} noValidate className="space-y-5">
            <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-4`}>
                <div>
                    <label className={labelCls} htmlFor="name">Imię i nazwisko *</label>
                    <input id="name" className={inputCls} value={form.name} onChange={onChange("name")} data-testid={`${testidPrefix}-name`} />
                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="company">Firma</label>
                    <input id="company" className={inputCls} value={form.company} onChange={onChange("company")} data-testid={`${testidPrefix}-company`} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="phone">Telefon</label>
                    <input id="phone" className={inputCls} value={form.phone} onChange={onChange("phone")} data-testid={`${testidPrefix}-phone`} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="email">E-mail *</label>
                    <input id="email" type="email" className={inputCls} value={form.email} onChange={onChange("email")} data-testid={`${testidPrefix}-email`} />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="industry">Branża</label>
                    <input id="industry" className={inputCls} value={form.industry} onChange={onChange("industry")} data-testid={`${testidPrefix}-industry`} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="website">Obecna strona</label>
                    <input id="website" className={inputCls} placeholder="np. www.firma.pl" value={form.website} onChange={onChange("website")} data-testid={`${testidPrefix}-website`} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="package">Pakiet</label>
                    <select id="package" className={inputCls} value={form.package} onChange={onChange("package")} data-testid={`${testidPrefix}-package`}>
                        <option value="">— Wybierz pakiet —</option>
                        <option value="Start">Start (od 2 190 zł)</option>
                        <option value="Rozwój">Rozwój (od 3 490 zł)</option>
                        <option value="Skala">Skala (od 6 490 zł)</option>
                        <option value="Prowadzenie social mediów – 799 zł / miesiąc">Prowadzenie social mediów (799 zł / miesiąc)</option>
                        <option value="Indywidualny">Indywidualna wycena</option>
                    </select>
                </div>
                <div id="timeline-options" data-testid={`${testidPrefix}-timeline-list`}>
                    <label className={labelCls} htmlFor="timeline">Termin</label>
                    <select id="timeline" className={inputCls} value={form.timeline} onChange={onChange("timeline")} data-testid={`${testidPrefix}-timeline`}>
                        <option value="">— Wybierz —</option>
                        <option>Pilnie (do 7 dni roboczych)</option>
                        <option>Do 2 tygodni</option>
                        <option>Do miesiąca</option>
                        <option>Brak konkretnego terminu</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls} htmlFor="budget">Orientacyjny budżet</label>
                    <select id="budget" className={inputCls} value={form.budget} onChange={onChange("budget")} data-testid={`${testidPrefix}-budget`}>
                        <option value="">— Wybierz przedział —</option>
                        <option>do 2 500 zł</option>
                        <option>2 500 – 5 000 zł</option>
                        <option>5 000 – 10 000 zł</option>
                        <option>powyżej 10 000 zł</option>
                        <option>Nie wiem, potrzebuję doradztwa</option>
                    </select>
                </div>
            </div>

            <div>
                <p className={labelCls}>Interesujące usługi</p>
                <div className="flex flex-wrap gap-2">
                    {allServices.map((s) => (
                        <button
                            type="button"
                            key={s}
                            onClick={() => toggleService(s)}
                            data-testid={`${testidPrefix}-service-${s}`}
                            className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                                form.services.includes(s)
                                    ? "border-[#14B8A6] bg-[#14B8A6]/10 text-[#0EA5A5]"
                                    : "border-[#E5E7EB] text-[#0B1220]/70 hover:border-[#14B8A6]/40"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className={labelCls} htmlFor="message">Opis projektu *</label>
                <textarea
                    id="message"
                    rows={5}
                    className={inputCls}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Opowiedz krótko o swojej firmie, celach i potrzebach…"
                    data-testid={`${testidPrefix}-message`}
                />
                {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
            </div>

            <label className="flex items-start gap-3 text-sm text-[#0B1220]/70 leading-relaxed" htmlFor="consent">
                <input id="consent" type="checkbox" checked={form.consent} onChange={onChange("consent")} className="mt-1 accent-[#14B8A6]" data-testid={`${testidPrefix}-consent`} />
                <span>
                    Wyrażam zgodę na przetwarzanie moich danych osobowych w celu kontaktu zwrotnego i przygotowania oferty. *
                </span>
            </label>
            {errors.consent && <p className="-mt-3 text-xs text-red-600">{errors.consent}</p>}

            <button
                type="submit"
                disabled={submitting}
                data-testid={`${testidPrefix}-submit`}
                className="w-full md:w-auto px-7 py-3.5 rounded-[12px] bg-[#14B8A6] hover:bg-[#0EA5A5] text-white font-semibold disabled:opacity-60 shadow-[0_10px_24px_-8px_rgba(20,184,166,0.55)] transition-all hover:-translate-y-0.5"
            >
                {submitting ? "Wysyłanie…" : "Wyślij zapytanie"}
            </button>
        </form>
    );
};
